import React, { useState, useEffect, useCallback } from 'react';
import { getPortings, updatePorting, downloadPortingsXlsx, getSellers } from '../services/api';

const STATUS_OPTIONS = [
  { value: 'INICIADA', label: 'Iniciada' },
  { value: 'EN_PROCESO', label: 'En Proceso' },
  { value: 'VENTA_COMPLETADA', label: 'Venta Completada' },
  { value: 'RECHAZADA', label: 'Rechazada' },
];

const STATUS_COLORS = {
  INICIADA: 'badge-gray',
  EN_PROCESO: 'badge-yellow',
  VENTA_COMPLETADA: 'badge-green',
  RECHAZADA: 'badge-red',
};

const PAGE_SIZE = 20;

function formatDate(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function EditModal({ porting, onClose, onSaved }) {
  const [status, setStatus] = useState(porting.status);
  const [callCenterNotes, setCallCenterNotes] = useState(porting.callCenterNotes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const data = {};
      if (status !== porting.status) data.status = status;
      if (callCenterNotes !== (porting.callCenterNotes || '')) data.callCenterNotes = callCenterNotes;
      if (Object.keys(data).length === 0) {
        onClose();
        return;
      }
      await updatePorting(porting.id, data);
      onSaved();
    } catch (err) {
      setError('Error al actualizar. Intenta de nuevo.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Editar Portacion #{porting.id}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          <div className="modal-info-grid">
            <div className="modal-info-item">
              <span className="modal-label">Vendedor</span>
              <span className="modal-value">{porting.user?.name || '-'}</span>
            </div>
            <div className="modal-info-item">
              <span className="modal-label">Cliente</span>
              <span className="modal-value">{porting.customerName} {porting.customerLastName}</span>
            </div>
            <div className="modal-info-item">
              <span className="modal-label">Telefono</span>
              <span className="modal-value mono">{porting.customerPhone}</span>
            </div>
            <div className="modal-info-item">
              <span className="modal-label">Compania</span>
              <span className="modal-value">{porting.phoneCompany}</span>
            </div>
            {porting.curp && (
              <div className="modal-info-item">
                <span className="modal-label">CURP</span>
                <span className="modal-value">{porting.curp}</span>
              </div>
            )}
            {porting.birthDate && (
              <div className="modal-info-item">
                <span className="modal-label">Fecha de nacimiento</span>
                <span className="modal-value">{porting.birthDate}</span>
              </div>
            )}
            {porting.birthState && (
              <div className="modal-info-item">
                <span className="modal-label">Estado de nacimiento</span>
                <span className="modal-value">{porting.birthState}</span>
              </div>
            )}
            <div className="modal-info-item">
              <span className="modal-label">Fecha</span>
              <span className="modal-value">{formatDate(porting.createdAt)}</span>
            </div>
            {porting.notes && (
              <div className="modal-info-item full-width">
                <span className="modal-label">Notas del vendedor</span>
                <span className="modal-value">{porting.notes}</span>
              </div>
            )}
          </div>

          <hr className="modal-divider" />

          {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

          <div className="modal-form-group">
            <label>Estatus</label>
            <select
              className="status-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              disabled={saving}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {status === 'VENTA_COMPLETADA' && porting.status !== 'VENTA_COMPLETADA' && (
              <p className="commission-hint">Se generara una comision de $100 MXN para el vendedor.</p>
            )}
          </div>

          <div className="modal-form-group">
            <label>Notas del Call Center</label>
            <textarea
              className="notes-textarea"
              value={callCenterNotes}
              onChange={(e) => setCallCenterNotes(e.target.value)}
              placeholder="Escribe notas del call center..."
              rows={3}
              disabled={saving}
            />
          </div>
        </div>

        <div className="modal-footer">
          <button className="modal-cancel-btn" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button className="modal-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function isSuperAdmin(user) {
  return user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN';
}

function PortingsView({ user }) {
  const superAdmin = isSuperAdmin(user);
  const [portings, setPortings] = useState([]);
  const [filter, setFilter] = useState('en_progreso');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingPorting, setEditingPorting] = useState(null);
  const [successId, setSuccessId] = useState(null);
  const [exporting, setExporting] = useState(false);
  // Working filter inputs
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sellerId, setSellerId] = useState('');
  // Applied filters (what the table/export actually use)
  const [aFrom, setAFrom] = useState('');
  const [aTo, setATo] = useState('');
  const [aSeller, setASeller] = useState('');
  const [sellers, setSellers] = useState([]);

  const fetchPortings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getPortings(filter, { q: search, page, pageSize: PAGE_SIZE, from: aFrom, to: aTo, sellerId: aSeller });
      setPortings(Array.isArray(data.portings) ? data.portings : []);
      setTotalPages(data.totalPages || 1);
      setTotal(data.total || 0);
    } catch (err) {
      setError('Error al cargar las portaciones. Intenta de nuevo.');
      console.error('Error fetching portings:', err);
    } finally {
      setLoading(false);
    }
  }, [filter, search, page, aFrom, aTo, aSeller]);

  useEffect(() => {
    fetchPortings();
  }, [fetchPortings]);

  // Load sellers for the "revisar vendedor" selector
  useEffect(() => {
    getSellers().then((d) => setSellers(d.sellers || [])).catch(() => {});
  }, []);

  // Reset to page 1 when filter or search changes
  const changeFilter = (f) => { setFilter(f); setPage(1); };
  const submitSearch = (e) => { e.preventDefault(); setSearch(q.trim()); setPage(1); };
  const clearSearch = () => { setQ(''); setSearch(''); setPage(1); };

  const applyFilters = () => { setAFrom(from); setATo(to); setASeller(sellerId); setPage(1); };
  const clearFilters = () => {
    setFrom(''); setTo(''); setSellerId('');
    setAFrom(''); setATo(''); setASeller(''); setPage(1);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadPortingsXlsx({ filter, q: search, from: aFrom, to: aTo, sellerId: aSeller });
    } catch (err) {
      setError('No se pudo exportar el Excel.');
    } finally {
      setExporting(false);
    }
  };

  const handleSaved = async () => {
    const savedId = editingPorting?.id;
    setEditingPorting(null);
    setSuccessId(savedId);
    setTimeout(() => setSuccessId(null), 2000);
    await fetchPortings();
  };

  const isFinalizadas = filter === 'finalizadas';

  return (
    <>
      <div className="filter-bar">
        <div className="filter-toggle">
          <button
            className={`filter-button ${filter === 'en_progreso' ? 'active' : ''}`}
            onClick={() => changeFilter('en_progreso')}
          >
            En progreso
          </button>
          <button
            className={`filter-button ${filter === 'finalizadas' ? 'active' : ''}`}
            onClick={() => changeFilter('finalizadas')}
          >
            Finalizadas
          </button>
        </div>
        <form className="search-form" onSubmit={submitSearch}>
          <input
            className="search-input"
            type="text"
            placeholder="Buscar cliente, teléfono, vendedor..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {search ? (
            <button type="button" className="refresh-button" onClick={clearSearch}>Limpiar</button>
          ) : (
            <button type="submit" className="refresh-button">Buscar</button>
          )}
        </form>
      </div>

      <div className="export-bar">
        <label className="date-field">Vendedor
          <select className="date-input" value={sellerId} onChange={(e) => setSellerId(e.target.value)}>
            <option value="">Todos los vendedores</option>
            {sellers.map((s) => (
              <option key={s.id} value={s.id}>{s.name}{s.businessCode ? ` · ${s.businessCode}` : ''}</option>
            ))}
          </select>
        </label>
        <label className="date-field">Desde
          <input type="date" className="date-input" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="date-field">Hasta
          <input type="date" className="date-input" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <button className="filter-apply-btn" onClick={applyFilters}>Filtrar</button>
        {(aFrom || aTo || aSeller) && (
          <button className="refresh-button" onClick={clearFilters}>Limpiar filtros</button>
        )}
        <button className="primary-button" style={{ marginLeft: 'auto' }} onClick={handleExport} disabled={exporting}>
          {exporting ? 'Generando...' : '⬇ Descargar Excel'}
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="table-container">
        {loading && portings.length === 0 ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Cargando portaciones...</p>
          </div>
        ) : portings.length === 0 ? (
          <div className="empty-state">
            <p>No hay portaciones {isFinalizadas ? 'finalizadas' : 'en progreso'}{search ? ` para "${search}"` : ''}.</p>
          </div>
        ) : (
          <table className="portings-table">
            <thead>
              <tr>
                <th>ID</th>
                {superAdmin && <th>Negocio</th>}
                <th>Vendedor</th>
                <th>Cliente</th>
                <th>Telefono</th>
                <th>Compania</th>
                <th>Estatus</th>
                <th>Fecha</th>
                {!isFinalizadas && <th style={{ width: 60, textAlign: 'center' }}>Editar</th>}
              </tr>
            </thead>
            <tbody>
              {portings.map((porting) => {
                const isSuccess = successId === porting.id;
                return (
                  <tr key={porting.id} className={isSuccess ? 'row-success' : ''}>
                    <td className="cell-id">#{porting.id}</td>
                    {superAdmin && (
                      <td>
                        {porting.user?.business?.name || '-'}
                        {porting.user?.business?.code
                          ? <span className="admin-email"> ({porting.user.business.code})</span>
                          : null}
                      </td>
                    )}
                    <td>{porting.user?.name || '-'}</td>
                    <td>{porting.customerName} {porting.customerLastName}</td>
                    <td className="cell-phone">{porting.customerPhone}</td>
                    <td>{porting.phoneCompany}</td>
                    <td>
                      <span className={`status-badge ${STATUS_COLORS[porting.status] || 'badge-gray'}`}>
                        {STATUS_OPTIONS.find((s) => s.value === porting.status)?.label || porting.status}
                      </span>
                    </td>
                    <td className="cell-date">{formatDate(porting.createdAt)}</td>
                    {!isFinalizadas && (
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className="edit-icon-btn"
                          title="Editar portacion"
                          onClick={() => setEditingPorting(porting)}
                        >
                          &#9998;
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="page-btn"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ← Anterior
          </button>
          <span className="page-info">Página {page} de {totalPages} · {total} registros</span>
          <button
            className="page-btn"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Siguiente →
          </button>
        </div>
      )}

      {editingPorting && (
        <EditModal
          porting={editingPorting}
          onClose={() => setEditingPorting(null)}
          onSaved={handleSaved}
        />
      )}
    </>
  );
}

export default PortingsView;
